import { useState, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';
import { MessageType } from '@zonofi/common';
import {
  usePublishMessageMutation,
  PublishMessagePayload,
  getWorkspaceId,
  dispatch,
  updateSendType,
  clearSelectedItemsMap,
  clearSelectedItemIds,
  useAppSelector,
  getUserData,
  PaymentInvoiceMap,
  getAppType,
  APP_TYPE,
} from '@zonofi/zono-money-store';
import { SendSelectedType } from '../types';
import { useNavigate } from 'react-router-dom';
import { SELLER_WEBAPP_ROUTES, FIELD_OPS_ROUTES } from '../constants';

const formatInvoices = (eachInvoice?: PaymentInvoiceMap[]) => {
  if (!Array.isArray(eachInvoice) || eachInvoice.length === 0) return '';
  if (typeof eachInvoice?.[0] === 'string') return eachInvoice.join(', ');
  return eachInvoice
    .map((eachObject) => eachObject?.docNumber)
    .filter(Boolean)
    .join(', ');
};

export const useSendSelectedCall = (
  selectedData: any,
  type?: SendSelectedType
) => {
  const navigate = useNavigate();
  const sellerWorkspaceId = useAppSelector(getWorkspaceId);
  const userData = useAppSelector(getUserData);
  const appType = useAppSelector(getAppType);

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [publishMessage, publishRes] = usePublishMessageMutation();

  const routeToNavigate = useMemo(
    () =>
      appType === APP_TYPE.FIELDS_OPS ? FIELD_OPS_ROUTES : SELLER_WEBAPP_ROUTES,
    [appType]
  );

  const getMessageType = (): MessageType | undefined => {
    switch (type) {
      case SendSelectedType.ORDER:
        return MessageType.ORDER_RECEIPT;
      case SendSelectedType.PAYMENT:
        return MessageType.PAYMENT_RECEIPT;
      case SendSelectedType.DISPACH_NOTE:
        return MessageType.DISPACH_NOTE;
      default:
        return undefined;
    }
  };

  const buildMessageData = (items: any[]): unknown[] => {
    if (!items?.length) return [];

    switch (type) {
      case SendSelectedType.ORDER:
        return items.map((order: any) => ({
          orderId: order.id,
          orderNumber: order.orderMetaData?.refOrderNumber ?? '',
          orderDate: order.documentDate ?? '',
          discount: order.discount ?? '0.00',
          total: order.total ?? '0.00',
          skuCount: order.skuCount ?? 1,
          poFileId: order.poFile?.id ?? '',
        }));

      case SendSelectedType.DISPACH_NOTE:
        return items.map((dispatch: any) => ({
          id: dispatch.id,
          dispatchDate: dispatch.dispatchDate,
          dispatchDocNumber: dispatch.dispatchDocNumber,
          termsOfDelivery: dispatch.termsOfDelivery ?? '',
          transporterName: dispatch.transporterName ?? '',
          type: dispatch.type || 'Invoice',
          vehicleNumber: dispatch.vehicleNumber ?? '',
        }));

      case SendSelectedType.PAYMENT:
        return items.map((item: any) => ({
          amount: item.amountPaid,
          invoiceRefCode: formatInvoices(item.invoices),
          paymentDate: item.paymentDate,
          paymentId: item.id,
          paymentRefCode: item.paymentRefCode ?? '',
          paymentType: item.paymentMode,
          status: item.status,
        }));

      default:
        return [];
    }
  };

  const onSendApiCall = async () => {
    if (selectedData?.length === 0) return;

    const messageType = getMessageType();
    if (!messageType) return;

    // Group selected items by inviteId or customerId and build data in one pass
    const groups = selectedData.reduce(
      (acc: Record<string, any[]>, item: any) => {
        const key = item.inviteId || item.customerId || '';
        if (!acc[key]) acc[key] = [];
        acc[key].push(item);
        return acc;
      },
      {}
    ) as Record<string, any[]>;

    const payloads: PublishMessagePayload[] = Object.entries(groups).map(
      ([key, items]) =>
        ({
          sellerWorkspaceId,
          inviteIds: [key],
          message: {
            type: messageType,
            _id: uuidv4(),
            cAt: moment().valueOf().toString(),
            chnType: 'C',
            _v: 2,
            source: 'system',
            data: buildMessageData(items),
            sId: userData?.id,
            user: {
              firstName: userData?.firstName ?? '',
              lastName: userData?.lastName ?? '',
              mobile: userData?.mobile ?? '',
              email: userData?.email ?? '',
              id: userData?.id,
            },
          },
        }) as PublishMessagePayload
    );

    try {
      await Promise.all(payloads.map((p) => publishMessage(p)));
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Failed to send some messages:', error);
    }
  };

  const onSuccessNavigate = () => {
    switch (type) {
      case SendSelectedType.PAYMENT:
        navigate(
          appType === APP_TYPE.FIELDS_OPS
            ? FIELD_OPS_ROUTES.PAYMENT
            : SELLER_WEBAPP_ROUTES.ACCEPT_PAYMENTS
        );
        break;
      case SendSelectedType.DISPACH_NOTE:
        navigate(routeToNavigate.DISPATCH_NOTES);
        break;

      default:
        navigate(-1);
        break;
    }
  };

  const onSuccessClose = () => {
    dispatch(updateSendType(''));
    dispatch(clearSelectedItemsMap());
    dispatch(clearSelectedItemIds());
    setShowSuccessModal(false);
    onSuccessNavigate();
  };

  return {
    onSendApiCall,
    isLoading: publishRes.isLoading,
    showSuccessModal,
    onSuccessClose,
  };
};
